import React from "react";
import { Instagram } from "lucide-react";
import { SESSION_MINUTES, SESSION_PRICE } from "../../../data/mentorshipData";
import { dayParts } from "../mentorshipUtils";
import { Avatar, Badge, CheckIcon, VerifiedIcon } from "./MentorParts";

// `onBook` renders the sticky "Book a call" CTA (mobile drill-down). On desktop
// the booking panel sits beside the profile instead, so it's omitted, and the
// availability strip is hidden via `showAvailability={false}`.
const MentorProfile = ({
  mentor,
  days = [],
  availabilityStatus = "ready",
  onBook,
  showAvailability = true,
}) => (
  <div className="ms-profile">
    <div className="ms-profile__head">
      <Avatar mentor={mentor} size={72} />
      <div className="ms-grow">
        <div className="ms-name ms-name--lg">
          {mentor.name} {mentor.verified && <VerifiedIcon size={16} />}
        </div>
        <div className="ms-cred">{mentor.cred}</div>
        <a
          className="ms-ig"
          href={`https://instagram.com/${mentor.ig}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Instagram size={13} aria-hidden="true" /> @{mentor.ig}
        </a>
      </div>
    </div>

    <div className="ms-badges ms-badges--spaced">
      {mentor.cats.map((cat) => (
        <Badge key={cat} tone="brand">
          {cat}
        </Badge>
      ))}
      {mentor.badges.map(([tone, label]) => (
        <Badge key={label} tone={tone}>
          {label}
        </Badge>
      ))}
    </div>

    <p className="ms-body ms-profile__tag">{mentor.tag}</p>

    <div className="ms-divider" />
    <h3 className="ms-h3">What you&apos;ll cover on the call</h3>
    <ul className="ms-check-list">
      {mentor.cover.map((item) => (
        <li key={item}>
          <span className="ms-ck">
            <CheckIcon />
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>

    {(mentor.videoUrl || mentor.video) && (
      <>
        <div className="ms-divider" />
        <div className="ms-video">
        {mentor.videoUrl ? (
          <video
            className="ms-video__player"
            controls
            preload="metadata"
            src={mentor.videoUrl}
          >
            Your browser does not support video playback.
          </video>
        ) : (
          <div className="ms-video__thumb">
            <div className="ms-video__play">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        )}
        {mentor.video && <div className="ms-video__cap">{mentor.video}</div>}
        </div>
      </>
    )}

    {showAvailability && (
      <>
        <div className="ms-divider" />
        <h3 className="ms-h3">Next availability</h3>
        {availabilityStatus === "loading" && (
          <p className="ms-small">Loading open times…</p>
        )}
        {availabilityStatus !== "loading" && days.length === 0 && (
          <p className="ms-small">No open times right now.</p>
        )}
        {days.length > 0 && (
          <div className="ms-slots">
            {days.slice(0, 5).map(({ date }) => {
              const { weekday, day } = dayParts(date);
              return (
                <div key={date} className="ms-slot">
                  <span className="d">{weekday}</span>
                  <span className="n">{day}</span>
                </div>
              );
            })}
          </div>
        )}
      </>
    )}

    {onBook && (
      <div className="ms-sticky">
        <button
          type="button"
          className="ms-btn ms-btn--primary ms-btn--lg ms-btn--block"
          onClick={onBook}
        >
          Book a call · ${SESSION_PRICE}
        </button>
        <p className="ms-small ms-center ms-mt-8">
          One-time payment. No subscription. {SESSION_MINUTES} minutes.
        </p>
      </div>
    )}
  </div>
);

export default MentorProfile;
